﻿import { Enumerable, Kbo } from "@models/configurators/configurator-di";
import { CToken } from "@models/configurators/ctoken";
import { KbObjectManager } from "@models/configurators/kb-object-manager";
import { UiObject } from "@models/configurators/ui-object";
import { Configurator } from "@models/configurators/configurator";
import { Field } from "@models/configurators/field";
import { Group } from "@models/configurators/group";
import { NestedSet } from "./nested-set";
import { icons } from "@tools";
import { IOverlayPopup } from "@models/entity-models";

@Kbo(CToken.OverlayPopup)
export class OverlayPopup extends UiObject implements IOverlayPopup {

    constructor(kom: KbObjectManager, obj?: any) {
        super(kom);

        this.icon = "";
        if (icons) {
            this.icon = icons.page;
        }
    }

    public icon: string;
    public width: string;
    public backgroundColor: string;
    public backgroundShade: string;
    public headerColor: string;
    public headerShade: string;

    private _enableClose: boolean;
    @Enumerable()
    public get enableClose(): boolean {
        return this._enableClose;
    }
    public set enableClose(val: boolean) {
        this.setProperty("enableClose", val);
    }

    public label: string;

    private _showLabel: boolean;
    @Enumerable()
    public get showLabel(): boolean {
        return this._showLabel;
    }
    public set showLabel(val: boolean) {
        this.setProperty("showLabel", val);
    }

    private _children: UiObject[];
    @Enumerable()
    public get children(): UiObject[] {
        if (!this._children) this._children = [];
        return this._children;
    }
    public set children(val) {
        this._children = val;
    }

    public get groups(): Group[] {
        return this.getChildrenOfType<Group>(CToken.Group);
    }

    private _overlayPopups: OverlayPopup[];
    @Enumerable()
    public get overlayPopups(): OverlayPopup[] {
        if (!this._overlayPopups) this._overlayPopups = [];
        return this._overlayPopups;
    }
    public set overlayPopups(val) {
        this._overlayPopups = val;
    }

    private _configurators: Configurator[];
    @Enumerable()
    public get configurators(): Configurator[] {
        if (!this._configurators) this._configurators = [];
        return this._configurators;
    }
    public set configurators(val) {
        this._configurators = val;
    }

    public addGroup(): Group {
        let group = new Group(this.$manager);
        this.$manager.addKbObject(group, this);
        this.children.push(group);
        return group;
    }

    public removeGroup(group: Group) {
        this.$manager.removeKbObject(group);
        this.children.remove(group);
    }

    public addOverlayPopup(): OverlayPopup {
        let overlayPopup = new OverlayPopup(this.$manager);
        this.$manager.addKbObject(overlayPopup, this);
        this.overlayPopups.push(overlayPopup);
        return overlayPopup;
    }

    public removeOverlayPopup(overlayPopup: OverlayPopup) {
        this.$manager.removeKbObject(overlayPopup);
        this.overlayPopups.remove(overlayPopup);
    }

    public allOverlayPopups(): OverlayPopup[] {
        return this.getChildrenOfType<OverlayPopup>(CToken.OverlayPopup);
    }

    public getFields(predicate?: (field: Field) => boolean): Field[] {
        let fields = this.getChildrenOfType<Field>(CToken.Field);
        return predicate ? fields.filter(f => predicate(f)) : fields;
    }

    public getChildrenOfType<T extends UiObject>(...tokens: CToken[]): T[] {
        return this.filterChildren<T>(tokens,
            this.children.concat(this.overlayPopups)
        );
    }

    public isValid(): boolean {
        let fields = this.getFields();
        let valid = !fields.some(f => !f.isValid());
        if (!valid) {
            this.$valid = false;
            return this.$valid;
        }

        for (let config of this.configurators) {
            if (!config.isValid()) {
                this.$valid = false;
                return this.$valid;
            }
        }
        let nestedSets = this.getChildrenOfType<NestedSet>(CToken.NestedSet);
        for (let ns of nestedSets) {
            for (let nested of ns.configurators) {
                if (!nested.isValid()) {
                    this.$valid = false;
                    return this.$valid;
                }
            }
        }
        this.$valid = true;
        return this.$valid;
    }

    public allowedChildren(rootType: CToken): CToken[] {
        let allowed = this.containerChildren(rootType);
        if (rootType == CToken.Configurator) {
            allowed.splice(0, 0, CToken.OverlayPopup);
        }
        return allowed;
    }

    public ignoredProperties(): string[] {
        return ["groups"];
    }
}
